import { api } from "../../lib";

// ============================================
// PRACTICE INTERFACES
// ============================================

export interface Practice {
  id: string;
  name: string;
  shortName: string | null;
  isActive: boolean;
  primaryPhone: string | null;
  secondaryPhone: string | null;
  fax: string | null;
  address: string | null;
  email: string | null;
  medicalDirector: string | null;
  footerHtml: string | null;
  preferredPathologistId: string | null;
  preferredPathologist?: { fullName: string } | null;
  logoS3Key: string | null;
  // HL7 / EHR Integration
  hl7EndpointHost: string | null;
  hl7EndpointPort: number | null;
  hl7FacilityId: string | null;
  hl7ApplicationId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePracticeInput {
  name: string;
  shortName?: string;
  isActive?: boolean;
  primaryPhone?: string;
  secondaryPhone?: string;
  fax?: string;
  address?: string | null;
  email?: string | null;
  medicalDirector?: string | null;
  footerHtml?: string | null;
  preferredPathologistId?: string;
  logoS3Key?: string | null;
  // HL7 / EHR Integration
  hl7EndpointHost?: string | null;
  hl7EndpointPort?: number | null;
  hl7FacilityId?: string | null;
  hl7ApplicationId?: string | null;
}

export interface UpdatePracticeInput extends Partial<CreatePracticeInput> {
  id: string;
}

// ============================================
// PRACTICE-PHYSICIAN INTERFACES
// ============================================

export interface PracticePhysician {
  id: string;
  practiceId: string;
  physicianId: string;
  isPrimary: boolean;
  createdAt: string;
  physician: {
    id: string;
    firstName: string;
    lastName: string;
    npi: string | null;
    email: string | null;
  };
}

export interface AddPracticePhysicianInput {
  physicianId: string;
  isPrimary?: boolean;
}

// ============================================
// PRACTICE REPORT DELIVERY METHOD INTERFACES
// ============================================

export interface PracticeReportDeliveryMethod {
  id: string;
  practiceId: string;
  reportDeliveryMethodId: string;
  phone: string | null;
  fax: string | null;
  address: string | null;
  email: string | null;
  isActive: boolean;
  // HL7 endpoint config (only for HL7 delivery methods)
  hl7EndpointHost: string | null;
  hl7EndpointPort: number | null;
  hl7FacilityId: string | null;
  hl7ApplicationId: string | null;
  remoteConnectionIdentifier?: string | null;
  createdAt: string;
  updatedAt: string;
  reportDeliveryMethod?: {
    id: string;
    name: string;
  };
}

export interface CreatePracticeReportDeliveryMethodInput {
  reportDeliveryMethodId: string;
  phone?: string | null;
  fax?: string | null;
  address?: string | null;
  email?: string | null;
  hl7EndpointHost?: string | null;
  hl7EndpointPort?: number | null;
  hl7FacilityId?: string | null;
  hl7ApplicationId?: string | null;
  remoteConnectionIdentifier?: string | null;
  physicianId?: string | null;
}

export interface UpdatePracticeReportDeliveryMethodInput {
  id: string;
  reportDeliveryMethodId?: string;
  phone?: string | null;
  fax?: string | null;
  address?: string | null;
  email?: string | null;
  hl7EndpointHost?: string | null;
  hl7EndpointPort?: number | null;
  hl7FacilityId?: string | null;
  hl7ApplicationId?: string | null;
  remoteConnectionIdentifier?: string | null;
}

// ============================================
// SERVICE
// ============================================

export const practicesService = {
  // ============================================
  // PRACTICE CRUD
  // ============================================

  getAll: async () => {
    return api.get<Practice[]>("/admin/practices");
  },

  getById: async (id: string) => {
    return api.get<Practice>(`/admin/practices/${id}`);
  },

  create: async (input: CreatePracticeInput) => {
    return api.post<Practice>("/admin/practices", input);
  },

  update: async (input: UpdatePracticeInput) => {
    const { id, ...updates } = input;
    return api.put<Practice>(`/admin/practices/${id}`, updates);
  },

  delete: async (id: string) => {
    await api.delete<void>(`/admin/practices/${id}`);
  },

  toggleActive: async (id: string, isActive: boolean) => {
    return api.put<Practice>(`/admin/practices/${id}`, { isActive });
  },

  // ============================================
  // PRACTICE-PHYSICIAN METHODS
  // ============================================

  getPhysicians: async (practiceId: string) => {
    return api.get<PracticePhysician[]>(
      `/admin/practices/${practiceId}/physicians`
    );
  },

  addPhysician: async (
    practiceId: string,
    input: AddPracticePhysicianInput
  ) => {
    return api.post<PracticePhysician>(
      `/admin/practices/${practiceId}/physicians`,
      input
    );
  },

  removePhysician: async (practiceId: string, physicianId: string) => {
    await api.delete<void>(
      `/admin/practices/${practiceId}/physicians/${physicianId}`
    );
  },

  setPrimaryPhysician: async (practiceId: string, physicianId: string) => {
    return api.patch<PracticePhysician>(
      `/admin/practices/${practiceId}/physicians/${physicianId}/set-primary`
    );
  },

  // ============================================
  // PRACTICE REPORT DELIVERY METHODS
  // ============================================

  getReportDeliveryMethods: async (practiceId: string) => {
    return api.get<PracticeReportDeliveryMethod[]>(
      `/admin/practices/${practiceId}/report-delivery-methods`
    );
  },

  addReportDeliveryMethod: async (
    practiceId: string,
    input: CreatePracticeReportDeliveryMethodInput
  ) => {
    return api.post<PracticeReportDeliveryMethod>(
      `/admin/practices/${practiceId}/report-delivery-methods`,
      input
    );
  },

  updateReportDeliveryMethod: async (
    practiceId: string,
    input: UpdatePracticeReportDeliveryMethodInput
  ) => {
    const { id, ...updates } =
      input as UpdatePracticeReportDeliveryMethodInput & {
        physicianId?: string | null;
      };
    delete updates.physicianId;
    return api.put<PracticeReportDeliveryMethod>(
      `/admin/practices/${practiceId}/report-delivery-methods/${id}`,
      updates
    );
  },

  removeReportDeliveryMethod: async (practiceId: string, id: string) => {
    await api.delete<void>(
      `/admin/practices/${practiceId}/report-delivery-methods/${id}`
    );
  },

  toggleReportDeliveryMethodActive: async (practiceId: string, id: string) => {
    return api.patch<PracticeReportDeliveryMethod>(
      `/admin/practices/${practiceId}/report-delivery-methods/${id}/toggle-active`
    );
  },

  // HL7 / EHR Integration
  testHl7Connection: async (practiceId: string) => {
    return api.post<{
      reachable: boolean;
      latencyMs?: number;
      error?: string;
      endpoint: string;
    }>(`/admin/practices/${practiceId}/test-hl7`);
  },

  // Logo upload
  getLogoPresignedUrl: async (
    fileName: string,
    contentType: string
  ): Promise<{ url: string; key: string }> => {
    return api.post<{ url: string; key: string }>(
      "/documents/presigned-url/practice-logos",
      { fileName, contentType }
    );
  },

  uploadLogoToS3: async (url: string, file: Blob): Promise<void> => {
    await fetch(url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type || "image/png" },
    });
  },

  getLogoDownloadUrl: async (key: string): Promise<string> => {
    const response = await api.post<{ url: string }>(
      "/documents/download-url",
      {
        key,
      }
    );
    return response.url;
  },
};
